<template>
  <div class="sv-page">
    <Card>
      <div class="toolbar">
        <Button type="primary" @click="openCreate">新建储值活动</Button>
        <Button class="m-l-10" @click="load">刷新</Button>
      </div>
      <Table class="m-t-16" :loading="loading" :columns="columns" :data="list" border stripe />
    </Card>

    <Modal v-model="showForm" :title="form._id ? '编辑储值活动' : '新建储值活动'" width="780" @on-ok="saveForm" @on-cancel="resetForm">
      <Form :label-width="120">
        <FormItem label="活动名称">
          <Input v-model="form.name" placeholder="如：新春储值加赠" />
        </FormItem>
        <FormItem label="状态">
          <Select v-model="form.status" style="width: 200px">
            <Option value="on">上架</Option>
            <Option value="off">下架</Option>
          </Select>
        </FormItem>
        <FormItem label="开始时间">
          <DatePicker v-model="form.start_at" type="datetime" placeholder="可选" style="width: 240px" />
        </FormItem>
        <FormItem label="结束时间">
          <DatePicker v-model="form.end_at" type="datetime" placeholder="可选" style="width: 240px" />
        </FormItem>
        <FormItem label="使用须知">
          <Input v-model="form.notice" type="textarea" :rows="3" placeholder="展示给用户的使用说明" />
        </FormItem>
        <Divider orientation="left">充值档位（元）</Divider>
        <div v-for="(t, i) in form.tiers" :key="'t-' + i" class="tier-row">
          <InputNumber v-model="t.recharge_yuan" :min="0.01" :step="1" placeholder="充值" style="width: 120px" />
          <span class="mx">充送</span>
          <InputNumber v-model="t.gift_yuan" :min="0" :step="1" placeholder="赠送" style="width: 120px" />
          <i-switch v-model="t.enabled" class="m-l-10" />
          <Button type="text" @click="form.tiers.splice(i, 1)">删除</Button>
        </div>
        <Button type="dashed" long @click="addTier" class="m-t-8">添加档位</Button>
        <FormItem label="自定义充值" class="m-t-16">
          <i-switch v-model="form.allow_custom_recharge" />
        </FormItem>
        <template v-if="form.allow_custom_recharge">
          <FormItem label="自定义下限(元)">
            <InputNumber v-model="form.custom_min_yuan" :min="0.01" style="width: 160px" />
          </FormItem>
          <FormItem label="自定义上限(元)">
            <InputNumber v-model="form.custom_max_yuan" :min="0.01" style="width: 160px" />
          </FormItem>
          <FormItem label="自定义赠送(万分比)">
            <InputNumber v-model="form.custom_gift_ratio_bp" :min="0" :max="10000" style="width: 160px" />
            <span class="hint">如 1000 = 送 10%</span>
          </FormItem>
        </template>
        <Divider orientation="left">消费规则</Divider>
        <FormItem label="可用商品">
          <Select v-model="form.consume_scope" style="width: 200px">
            <Option value="all">全部商品</Option>
            <Option value="include">指定商品</Option>
          </Select>
        </FormItem>
        <FormItem v-if="form.consume_scope === 'include'" label="已选商品">
          <div class="food-picked">
            <Tag
              v-for="id in form.food_ids"
              :key="id"
              closable
              @on-close="removeFoodId(id)"
              class="food-tag"
            >
              {{ foodNameOf(id) }}
            </Tag>
            <span v-if="!form.food_ids.length" class="hint-inline">尚未选择商品</span>
          </div>
          <div class="m-t-8">
            <Button type="dashed" icon="ios-list" @click="openFoodPicker">从商品列表勾选</Button>
            <span class="hint">已选 {{ form.food_ids.length }} 个</span>
          </div>
        </FormItem>
        <Divider orientation="left">优惠叠加</Divider>
        <FormItem label="储值+优惠券">
          <i-switch v-model="form.stack_balance_coupon" />
        </FormItem>
        <FormItem label="储值+会员价">
          <i-switch v-model="form.stack_balance_member_price" />
        </FormItem>
        <FormItem label="券+会员价">
          <i-switch v-model="form.stack_coupon_member_price" />
        </FormItem>
        <FormItem label="备注">
          <Input v-model="form.remark" type="textarea" :rows="2" />
        </FormItem>
      </Form>
    </Modal>

    <!-- 商品勾选弹窗 -->
    <Modal
      v-model="showFoodPicker"
      title="选择可用商品"
      width="760"
      :mask-closable="false"
      @on-ok="confirmFoodPick"
      @on-cancel="closeFoodPicker"
    >
      <div class="food-picker-toolbar">
        <Input
          v-model="foodPickerKey"
          search
          enter-button="搜索"
          placeholder="商品名称"
          style="width: 280px"
          @on-search="searchFoodPicker"
        />
        <span class="hint">勾选后点确定；可多选</span>
      </div>
      <Table
        class="m-t-12"
        ref="foodPickerTable"
        :loading="foodPickerLoading"
        :columns="foodPickerColumns"
        :data="foodPickerList"
        :height="360"
        border
        @on-selection-change="onFoodPickerSelection"
      />
      <Page
        class="m-t-12"
        v-show="foodPickerTotal > 0"
        :total="foodPickerTotal"
        :current="foodPickerPage"
        :page-size="foodPickerPageSize"
        show-total
        @on-change="onFoodPickerPage"
      />
    </Modal>
  </div>
</template>

<script>
import {
  storedValueActivityList,
  storedValueActivityCreate,
  storedValueActivityUpdate,
} from '@/api/storedValue'
import { goodsPage } from '@/api/goods'

function emptyForm() {
  return {
    _id: '',
    name: '',
    status: 'on',
    start_at: null,
    end_at: null,
    notice: '',
    allow_custom_recharge: false,
    custom_min_yuan: 1,
    custom_max_yuan: 5000,
    custom_gift_ratio_bp: 0,
    tiers: [{ recharge_yuan: 100, gift_yuan: 20, enabled: true }],
    consume_scope: 'all',
    food_ids: [],
    stack_balance_coupon: true,
    stack_balance_member_price: true,
    stack_coupon_member_price: true,
    remark: '',
  }
}

function yuanToFen(y) {
  const n = Number(y)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.round(Number(n.toFixed(2)) * 100)
}

function fenToYuan(f) {
  const n = Math.round(Number(f) || 0)
  return n / 100
}

function foodIdOf(row) {
  if (!row) return ''
  return row._id != null ? String(row._id) : ''
}

export default {
  name: 'StoredValue',
  data() {
    return {
      loading: false,
      list: [],
      showForm: false,
      form: emptyForm(),
      foodNameMap: {},
      showFoodPicker: false,
      foodPickerLoading: false,
      foodPickerList: [],
      foodPickerTotal: 0,
      foodPickerPage: 1,
      foodPickerPageSize: 10,
      foodPickerKey: '',
      foodPickerSelectedIds: [],
      foodPickerColumns: [
        { type: 'selection', width: 56, align: 'center' },
        {
          title: '商品',
          key: 'name',
          minWidth: 160,
          render: (h, { row }) => h('span', row.name || row.food_name || '—'),
        },
        {
          title: '价格',
          key: 'price',
          width: 100,
          render: (h, { row }) => h('span', row.price != null ? `￥${row.price}` : '—'),
        },
        {
          title: '库存',
          key: 'num',
          width: 80,
          render: (h, { row }) => h('span', row.num != null ? String(row.num) : '—'),
        },
        {
          title: '状态',
          key: 'status',
          width: 80,
          render: (h, { row }) =>
            h('span', !row.status || row.status === 'up' ? '上架' : '下架'),
        },
      ],
      columns: [
        { title: '名称', key: 'name', minWidth: 140 },
        {
          title: '状态',
          key: 'status',
          width: 80,
          render: (h, { row }) => h('span', row.status === 'on' ? '上架' : '下架'),
        },
        {
          title: '档位',
          key: 'tiers',
          minWidth: 200,
          render: (h, { row }) => {
            const tiers = (row.tiers || []).filter((t) => t && t.enabled !== false)
            const text = tiers
              .map((t) => `充${fenToYuan(t.recharge_fen)}送${fenToYuan(t.gift_fen)}`)
              .join('；')
            return h('span', text || '-')
          },
        },
        {
          title: '自定义充值',
          key: 'allow_custom_recharge',
          width: 100,
          render: (h, { row }) => h('span', row.allow_custom_recharge ? '是' : '否'),
        },
        {
          title: '商品范围',
          key: 'consume_scope',
          width: 100,
          render: (h, { row }) => h('span', row.consume_scope === 'include' ? '指定商品' : '全部'),
        },
        {
          title: '操作',
          key: 'action',
          width: 100,
          render: (h, { row }) =>
            h(
              'Button',
              { props: { type: 'primary', size: 'small' }, on: { click: () => this.openEdit(row) } },
              '编辑'
            ),
        },
      ],
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    foodNameOf(id) {
      const sid = String(id)
      return this.foodNameMap[sid] || sid.slice(0, 8) + '…'
    },
    async load() {
      this.loading = true
      try {
        const res = await storedValueActivityList()
        if (res.data && res.data.code === 200) {
          this.list = res.data.data || []
        } else {
          this.$Message.error((res.data && res.data.msg) || '加载失败')
        }
      } catch (e) {
        this.$Message.error(e.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.form = emptyForm()
      this.foodNameMap = {}
      this.showForm = true
    },
    async openEdit(row) {
      const food_ids = Array.isArray(row.food_ids)
        ? row.food_ids.map((x) => String(x)).filter(Boolean)
        : []
      this.form = {
        _id: row._id,
        name: row.name || '',
        status: row.status === 'off' ? 'off' : 'on',
        start_at: row.start_at ? new Date(row.start_at) : null,
        end_at: row.end_at ? new Date(row.end_at) : null,
        notice: row.notice || '',
        allow_custom_recharge: !!row.allow_custom_recharge,
        custom_min_yuan: fenToYuan(row.custom_min_fen || 100),
        custom_max_yuan: fenToYuan(row.custom_max_fen || 500000),
        custom_gift_ratio_bp: Number(row.custom_gift_ratio_bp) || 0,
        tiers: (row.tiers || []).map((t) => ({
          recharge_yuan: fenToYuan(t.recharge_fen),
          gift_yuan: fenToYuan(t.gift_fen),
          enabled: t.enabled !== false,
        })),
        consume_scope: row.consume_scope === 'include' ? 'include' : 'all',
        food_ids,
        stack_balance_coupon: row.stack_balance_coupon !== false,
        stack_balance_member_price: row.stack_balance_member_price !== false,
        stack_coupon_member_price: row.stack_coupon_member_price !== false,
        remark: row.remark || '',
      }
      if (!this.form.tiers.length) {
        this.form.tiers = [{ recharge_yuan: 100, gift_yuan: 0, enabled: true }]
      }
      this.showForm = true
      if (food_ids.length) this.resolveFoodNames(food_ids)
    },
    async resolveFoodNames(ids) {
      const need = (ids || []).map(String).filter((id) => id && !this.foodNameMap[id])
      if (!need.length) return
      try {
        // 拉一页较大列表做名称映射；未命中的保留短 id 展示
        const res = await goodsPage({ pageNo: 1, pageSize: 500 })
        if (res.data && res.data.code === 200 && Array.isArray(res.data.data)) {
          const map = { ...this.foodNameMap }
          res.data.data.forEach((row) => {
            const id = foodIdOf(row)
            if (id) map[id] = row.name || row.food_name || id
          })
          this.foodNameMap = map
        }
      } catch (_) {}
    },
    removeFoodId(id) {
      const sid = String(id)
      this.form.food_ids = (this.form.food_ids || []).filter((x) => String(x) !== sid)
    },
    openFoodPicker() {
      this.foodPickerSelectedIds = [...(this.form.food_ids || [])]
      this.foodPickerPage = 1
      this.foodPickerKey = ''
      this.showFoodPicker = true
      this.loadFoodPicker()
    },
    closeFoodPicker() {
      this.showFoodPicker = false
    },
    searchFoodPicker() {
      this.foodPickerPage = 1
      this.loadFoodPicker()
    },
    onFoodPickerPage(p) {
      this.foodPickerPage = p
      this.loadFoodPicker()
    },
    onFoodPickerSelection(rows) {
      const pageIds = (this.foodPickerList || []).map(foodIdOf).filter(Boolean)
      const selectedOnPage = new Set((rows || []).map(foodIdOf).filter(Boolean))
      // 保留其他页已选，替换当前页勾选结果
      const kept = (this.foodPickerSelectedIds || []).filter((id) => !pageIds.includes(String(id)))
      this.foodPickerSelectedIds = kept.concat([...selectedOnPage])
    },
    async loadFoodPicker() {
      this.foodPickerLoading = true
      try {
        const res = await goodsPage({
          pageNo: this.foodPickerPage,
          pageSize: this.foodPickerPageSize,
          key: this.foodPickerKey || undefined,
        })
        if (res.data && res.data.code === 200) {
          const selected = new Set((this.foodPickerSelectedIds || []).map(String))
          const rows = res.data.data || []
          this.foodPickerList = rows.map((row) => ({
            ...row,
            _checked: selected.has(foodIdOf(row)),
          }))
          this.foodPickerTotal = Number(res.data.total) || 0
          const map = { ...this.foodNameMap }
          this.foodPickerList.forEach((row) => {
            const id = foodIdOf(row)
            if (id) map[id] = row.name || row.food_name || id
          })
          this.foodNameMap = map
        } else {
          this.$Message.error((res.data && res.data.msg) || '加载商品失败')
        }
      } catch (e) {
        this.$Message.error(e.message || '加载商品失败')
      } finally {
        this.foodPickerLoading = false
      }
    },
    confirmFoodPick() {
      const ids = Array.from(new Set((this.foodPickerSelectedIds || []).map(String).filter(Boolean)))
      this.form.food_ids = ids
      this.showFoodPicker = false
    },
    addTier() {
      this.form.tiers.push({ recharge_yuan: 100, gift_yuan: 0, enabled: true })
    },
    resetForm() {
      this.form = emptyForm()
      this.foodNameMap = {}
    },
    buildPayload() {
      const food_ids =
        this.form.consume_scope === 'include'
          ? (this.form.food_ids || []).map(String).filter(Boolean)
          : []
      return {
        name: this.form.name,
        status: this.form.status,
        start_at: this.form.start_at || null,
        end_at: this.form.end_at || null,
        notice: this.form.notice,
        allow_custom_recharge: !!this.form.allow_custom_recharge,
        custom_min_fen: yuanToFen(this.form.custom_min_yuan),
        custom_max_fen: yuanToFen(this.form.custom_max_yuan),
        custom_gift_ratio_bp: Math.round(Number(this.form.custom_gift_ratio_bp) || 0),
        tiers: (this.form.tiers || []).map((t, i) => ({
          recharge_fen: yuanToFen(t.recharge_yuan),
          gift_fen: yuanToFen(t.gift_yuan),
          enabled: t.enabled !== false,
          sort: i,
        })),
        consume_scope: this.form.consume_scope,
        food_ids,
        stack_balance_coupon: !!this.form.stack_balance_coupon,
        stack_balance_member_price: !!this.form.stack_balance_member_price,
        stack_coupon_member_price: !!this.form.stack_coupon_member_price,
        remark: this.form.remark,
      }
    },
    async saveForm() {
      if (!this.form.name || !String(this.form.name).trim()) {
        this.$Message.warning('请填写活动名称')
        return false
      }
      if (this.form.consume_scope === 'include' && !(this.form.food_ids && this.form.food_ids.length)) {
        this.$Message.warning('请勾选至少一个可用商品')
        return false
      }
      const payload = this.buildPayload()
      try {
        let res
        if (this.form._id) {
          res = await storedValueActivityUpdate(this.form._id, payload)
        } else {
          res = await storedValueActivityCreate(payload)
        }
        if (res.data && res.data.code === 200) {
          this.$Message.success('保存成功')
          this.showForm = false
          this.load()
        } else {
          this.$Message.error((res.data && res.data.msg) || '保存失败')
          return false
        }
      } catch (e) {
        this.$Message.error(e.message || '保存失败')
        return false
      }
    },
  },
}
</script>

<style scoped>
.toolbar { display: flex; align-items: center; }
.m-l-10 { margin-left: 10px; }
.m-t-8 { margin-top: 8px; }
.m-t-12 { margin-top: 12px; }
.m-t-16 { margin-top: 16px; }
.tier-row { display: flex; align-items: center; margin-bottom: 8px; }
.mx { margin: 0 8px; }
.hint { margin-left: 8px; color: #999; font-size: 12px; }
.hint-inline { color: #999; font-size: 12px; }
.food-picked { min-height: 32px; }
.food-tag { margin: 0 8px 8px 0; }
.food-picker-toolbar { display: flex; align-items: center; justify-content: space-between; }
</style>
